
import { defineComponent, PropType, computed } from "vue";

import DisplayBlock from "./fields/DisplayBlock.vue";
import ImageInput from "./fields/ImageInput.vue";
import TextInput from "./fields/TextInput.vue";
import TextareaInput from "./fields/TextareaInput.vue";
import HtmlEditorInput from "./fields/HtmlEditorInput.vue";
import CheckboxInput from "./fields/CheckboxInput.vue";
import PasswordInput from "./fields/PasswordInput.vue";
import CheckboxesGroup from "./fields/CheckboxesGroup.vue";
import DateInput from "./fields/DateInput.vue";
import SelectInput from "./fields/SelectInput.vue";
import SelectedOrderChips from "./fields/SelectedOrderChips.vue";
import MapInput from "./fields/MapInput.vue";
import NestedArrayInput from "./fields/NestedArrayInput.vue";
import ChipsNumberArrayInput from "./fields/ChipsNumberArrayInput.vue";
import GameSelectWithPicker from "./fields/pickers/GameSelectWithPicker.vue";
import ActionButtonsInput from "./fields/ActionButtonsInput.vue";

type RelationshipsMap = Record<string, Array<Record<string, unknown>>>;

export default defineComponent({
  name: "InputsRenderer",
  components: {
    DisplayBlock,
    ImageInput,
    TextInput,
    TextareaInput,
    HtmlEditorInput,
    CheckboxInput,
    PasswordInput,
    CheckboxesGroup,
    DateInput,
    SelectInput,
    MapInput,
    NestedArrayInput,
    ChipsNumberArrayInput,
    GameSelectWithPicker,
    SelectedOrderChips,
    ActionButtonsInput,
    
    
  },
  props: {
    routeKey: { type: String, required: true },
    id: { type: String, required: true },
    inputs: { type: Array as PropType<any[]>, required: true },
    relationships: { type: Object as PropType<RelationshipsMap>, required: false },
    values: { type: Object as PropType<Record<string, any>>, required: true },
  },
  emits: ["update:values", "add-nested-row", "remove-nested-row", "add-tier", "remove-tier"],
  setup(props, { emit }) {
    // Cache computed models per key so we don’t create new computeds every render
    const cache = new Map<string, any>();
    const isEditMode = computed(() => {
      const numericId = Number(props.id);
      return !!props.id && !isNaN(numericId) && numericId > 0;
    });

    const cloneAndSet = (key: string, val: any) => {
      // shallow clone is enough for form root
      const next = { ...props.values, [key]: val };
      emit("update:values", next);
    };

    const fieldModel = (key?: string) => {
      const k = key ?? "";
      if (!k) return computed({ get: () => undefined, set: () => void 0 });

      if (!cache.has(k)) {
        cache.set(
          k,
          computed({
            get: () => props.values?.[k],
            set: (v) => cloneAndSet(k, v),
          })
        );
      }
      return cache.get(k);
    };

    const isText = (input: any) => {
      if (!showByCondition(input)) return false;
      return ["text", "decimal", "textFromJson"].includes(input.type);
    };

    const showByCondition = (input: any) => {
      const condOk = input.hasDisplayConditions ? input.displayCondition(props.values) : true;
      const relOk = !input.displayRelated || props.values[input.displayRelated];
      return !!condOk && !!relOk;
    };

    const isRelationshipSelect = (input: any) =>
      input.type === "select" &&
      showByCondition(input) &&
      props.relationships &&
      props.relationships[input.name];

    const isCustomSelect = (input: any) =>
      (input.type === "selectCustom" || input.type === "selectCustomFromJson") &&
      showByCondition(input);

    // Pass-through events upwards
    const emitAddNestedRow = (fieldName: string) => emit("add-nested-row", fieldName);
    const emitRemoveNestedRow = (fieldName: string, index: number) =>
      emit("remove-nested-row", { fieldName, index });
    const emitAddTier = (fieldName: string, value?: number) => emit("add-tier", { fieldName, value });
    const emitRemoveTier = (fieldName: string, index: number) => emit("remove-tier", { fieldName, index });
    const setField = (key: string, val: unknown) => {
        const next = { ...props.values, [key]: val };
        emit("update:values", next);
    };
    

    const getField = (key: string) => {
        return props.values?.[key];
    };
    return {
      getField,
      setField,
      fieldModel,
      isText,
      showByCondition,
      isRelationshipSelect,
      isCustomSelect,
      emitAddNestedRow,
      emitRemoveNestedRow,
      emitAddTier,
      emitRemoveTier,
      isEditMode,
    };
  },
});
